import { createAppKit } from '@reown/appkit'
import { EthersAdapter } from '@reown/appkit-adapter-ethers'
import { bsc } from '@reown/appkit/networks'

// Single AppKit instance + adapter reference, shared across all components.
// createAppKit must only be called once per page.
let _modal   = null
let _adapter = null

export function getAppKit() {
    if (_modal) return _modal

    const pid = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID
    if (!pid) throw new Error('WalletConnect is not configured. Please contact support.')

    // Keep a direct reference to the adapter so we can read walletProvider
    // without going through AppKit's base-client lookup (which requires
    // ChainController.activeChain to be resolved — sometimes it isn't yet).
    _adapter = new EthersAdapter()

    _modal = createAppKit({
        adapters:       [_adapter],
        projectId:      pid,
        networks:       [bsc],
        defaultNetwork: bsc,
        metadata: {
            name:        document.title || 'Finance',
            description: 'BSC USDT BEP-20',
            url:         window.location.origin,
            icons:       [`${window.location.origin}/favicon.ico`],
        },
        features:       { analytics: false, email: false, socials: false },
        themeMode:      'dark',
        themeVariables: { '--w3m-z-index': '9999' },
    })

    return _modal
}

// Probe all available provider sources in order of reliability.
//
// Why _adapter.walletProvider first:
//   EthersAdapter.setWalletConnectProvider() sets walletProvider = universalProvider
//   BEFORE syncAccount() runs and fires subscribeAccount. So by the time our
//   subscribeAccount callback executes, this field is guaranteed to be set.
//
//   AppKit's getWalletProvider() looks up ProviderController via
//   ChainController.state.activeChain as the namespace key. That key may still be
//   null/mismatched at the moment subscribeAccount fires, causing it to return null.
function findProvider() {
    // 1. Most reliable: direct adapter field, set before subscribeAccount fires
    if (_adapter?.walletProvider) return _adapter.walletProvider
    // 2. AppKit base method (fallback; may fail if activeChain not yet resolved)
    try { const p = _modal?.getWalletProvider?.(); if (p) return p } catch {}
    // 3. Injected browser extension (MetaMask, Trust, OKX, Binance, etc.)
    if (window.ethereum) {
        console.debug('[AppKit] Found injected provider (window.ethereum)')
        return window.ethereum
    }
    console.debug('[AppKit] No provider found via adapter/AppKit or injected window.ethereum')
    return null
}

// Open AppKit connect modal; resolves { provider, address } on connection.
// Rejects if the user closes without connecting.
export function openWcModal() {
    return new Promise((resolve, reject) => {
        let modal
        try { modal = getAppKit() } catch (e) { return reject(e) }

        // Fast-path: reuse an existing active session without reopening the modal.
        const existingAddr = modal.getAddress?.()
        if (existingAddr) {
            console.debug('[AppKit] openWcModal: existing session address detected', existingAddr)
            const p = findProvider()
            if (p) return resolve({ provider: p, address: existingAddr })
            console.warn('[AppKit] openWcModal: existing address found but provider lookup failed')
        }

        try { modal.close() } catch {}

        let done      = false
        let hasOpened = false

        // Initialise to no-ops so finish() is safe even when subscribeAccount fires
        // its callback synchronously (active session already present). Using let +
        // reassignment avoids the temporal dead zone that const would create here.
        let unsubAccount = () => {}
        let unsubState   = () => {}

        const finish = (fn) => {
            if (done) return
            done = true
            try { if (typeof unsubAccount === 'function') unsubAccount() } catch (e) { console.warn('[AppKit] Error unsubscribing from account:', e) }
            try { if (typeof unsubState === 'function') unsubState() } catch (e) { console.warn('[AppKit] Error unsubscribing from state:', e) }
            if (typeof fn === 'function') {
                try { fn() } catch (e) { console.error('[AppKit] Error in finish callback:', e); reject(e) }
            } else {
                console.error('[AppKit] finish() called with non-function:', fn)
            }
        }

        // Retry provider lookup up to MAX times.
        // _adapter.walletProvider is set synchronously before subscribeAccount fires,
        // but we retry as a safety net for unexpected timing on slow devices.
        const giveProvider = (address) => {
            const MAX = 10
            const GAP = 200 // ms between retries
            let tries = 0

            const tryOnce = () => {
                const p = findProvider()
                if (p) { resolve({ provider: p, address }); return }
                if (++tries < MAX) setTimeout(tryOnce, GAP)
                else reject(new Error('Wallet connected but provider not available. Please try again.'))
            }
            tryOnce()
        }

        // ── Primary success path ────────────────────────────────────────────
        // subscribeAccount fires after adapter.setWalletConnectProvider() completes,
        // so _adapter.walletProvider is guaranteed to be set at this point.
        try {
            unsubAccount = modal.subscribeAccount((account) => {
                console.debug('[AppKit] subscribeAccount event', account)
                if (!account?.address || done) return
                finish(() => giveProvider(account.address))
            }) ?? (() => {})
        } catch (e) {
            console.error('[AppKit] Failed to subscribe to account:', e)
        }

        // ── Cancellation / session-resume fallback ──────────────────────────
        // Modal closes: if subscribeAccount hasn't fired, user cancelled.
        // 700ms grace window covers slight ordering differences between the
        // modal-close event and account-sync completion.
        try {
            unsubState = modal.subscribeState((state) => {
                console.debug('[AppKit] subscribeState event', state)
                if (state.open) { hasOpened = true; return }

                if (hasOpened && !state.open && !done) {
                    setTimeout(() => {
                        if (done) return
                        const addr = modal.getAddress?.()
                        if (addr) finish(() => giveProvider(addr))
                        else      finish(() => reject(new Error('Wallet connection cancelled')))
                    }, 700)
                }
            }) ?? (() => {})
        } catch (e) {
            console.error('[AppKit] Failed to subscribe to state:', e)
        }

        // Only open the modal if not already resolved by a synchronous callback above
        if (!done) {
            try { modal.open({ view: 'Connect' }) } catch (e) { console.error('[AppKit] Failed to open modal:', e); reject(e) }
        }
    })
}
